import React from 'react';
import { useSelector } from 'react-redux';
import { getAllLayers } from 'pxc-editor-core/helpers/schema';
import { NON_BREAKING_SPACE_SYMBOL, DEFAULT_LINE_SPACE_FACTOR } from '@/constants';
import { generateFontString } from '../../../helpers/general';
import { getElementByGetElementById } from 'pxc-editor-web/src/helpers';

const toKebabCase = str => {
  return str.replace(/[A-Z]/g, function(match) {
    return '-' + match.toLowerCase();
  });
};

const Text = props => {
  const { id, rectProps, formatProps, resizeType } = props;
  const { x, y, width, height } = rectProps;
  const {
    text,
    fontColor,
    isBold,
    isItalic,
    isUnderline,
    font,
    fontSize,
    fontWidthFactor,
    lineSpaceFactor=DEFAULT_LINE_SPACE_FACTOR,
    horizontalAlignment,
    verticalAlignment,
    shieldId,
  } = formatProps;
  const measureNode = getElementByGetElementById('measure-node');
  const fontSizeCoef = useSelector(state => state.core.fontSizeCoef);
  const letterSpacingCoef = useSelector(state => state.core.letterSpacingCoef);
  const pages = useSelector(state => state.core.pages);
  const { shield } = getAllLayers(shieldId, pages);
  const fontCoef = fontSizeCoef[font];
  const fontSizeMm = fontSize * fontCoef;
  const lineHeight = fontSizeMm * lineSpaceFactor;
  const fontString = generateFontString(isBold, isItalic, fontSizeMm, font);
  const shouldCalculate = (resizeType && resizeType === '@/ENDLESS_FIXED');
  const fill = fontColor;
  const style = {
    whiteSpace: 'pre',
    font: fontString,
    fill,
    textDecoration: isUnderline ? 'underline' : 'none',
    letterSpacing: `${letterSpacingCoef}px`,
  };
  if (measureNode) {
    measureNode.style = Object.entries(style)
      .map(([key, value]) => `${toKebabCase(key)}: ${value}`)
      .join(';');
  }
  const lines = shouldCalculate
    ? calculateLinesBin(text, width / fontWidthFactor, id, measureNode)
    : text.split('\n');
  // Visual block height: one glyph height (fontSizeMm) plus a lineHeight gap for
  // each additional line. Using lineHeight * lines.length would over-count by the
  // extra leading of one line and skew centering as lineSpaceFactor grows.
  const textBlockHeight = fontSizeMm + Math.max(lines.length - 1, 0) * lineHeight;
  const posY = getTextY(
    y,
    height,
    fontSizeMm,
    textBlockHeight,
    verticalAlignment
  );

  const xScalingOffset = x * (1 - fontWidthFactor);
  const transformStr = `translate(${xScalingOffset}, 0) scale(${fontWidthFactor}, 1)`;

  return (
    <text
      id={id}
      key={id}
      x={x}
      y={posY}
      dominantBaseline="baseline"
      style={style}
      transform={transformStr}
    >
      {lines.map((text, index) => {
        const content = text || NON_BREAKING_SPACE_SYMBOL;
        return (
          <tspan
            key={index}
            id={`${id}-${index}-tspan`}
            fontSize={fontSizeMm}
            dy={index === 0 ? fontSizeMm : lineHeight}
            {...generateTSpanProps(x, width, fontWidthFactor, horizontalAlignment, shield)}
          >
            {content}
          </tspan>
        );
      })}
    </text>
  );
};

export default Text;

const getTextWidth = (text, id, measureNode, cycles) => {
  const renderer =
    measureNode ?? document.getElementById(id.replace('text', 'measure'));
  if (!renderer) return 0;
  renderer.textContent = text;
  const result = renderer.getComputedTextLength();
  return result;
};

const calculateLinesBin = (text, width, id, measureNode) => {
  const lines = [];

  const paragraphs = text.split('\n');
  for (let i = 0; i < paragraphs.length; i++) {
    let p = paragraphs[i];

    // 1. paragraph as a whole
    let len = getTextWidth(p, id, measureNode);
    if (len <= width) {
      lines.push(p);
      continue;
    }

    // TODO: for a single word case complex unicode symbols (emojis, Hindi, etc.) may be broken
    // TODO: see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Segmenter
    // 2. split into words first
    let words = p.split(' ');
    let wordCounter = 0;
    while (words.length > 0) {
      let splitPointWords = splitLineByWords(words, width, id, measureNode);
      if (splitPointWords > 0) {
        // FIXME: we do not preserve original whitespaces
        lines.push(joinWords(words, splitPointWords));
        words = words.slice(splitPointWords);
      } else {
        if (splitPointWords === 0 && words.length > 0) {
          // there is big word in the beginning, that does not fit itself
          let bigWord = words.shift();
          let letterCounter = 0;
          if (bigWord.length > 0) {
            let splitPointLetters = splitWordByLetters(
              bigWord,
              width,
              id,
              measureNode
            );
            if (splitPointLetters > 0) {
              lines.push(bigWord.substring(0, splitPointLetters));
              bigWord = bigWord.substring(splitPointLetters);
            } else {
              // that means not even single letter fit

              // take one symbol at a time anyway
              lines.push(bigWord.substring(0, 1));
              bigWord = bigWord.substring(1);
            }

            // return remaining part of bigWord back to words and try split by words again
            if (bigWord.length > 0) {
              words.unshift(bigWord);
            }
          }
        } else {
          // should never happen
          console.error(
            'splitLineByWords: should never happen: returned ' + splitPointWords
          );
        }
      }
    }
  }

  return lines;
};

function joinWords(words, split) {
  return words.slice(0, split).join(' ');
}

function binarySplit(wordOrWords, width, id, measureNode, makeLineFunction) {
  let cycles = 0;
  let lo = 0;
  let hi = wordOrWords.length;

  while (lo <= hi) {
    let mid = lo + Math.floor((hi - lo) / 2);
    let line = makeLineFunction(wordOrWords, mid);
    let len = getTextWidth(line, id, measureNode, cycles);

    if (len > width) hi = mid - 1;
    else if (len < width) lo = mid + 1;
    else return mid;

    cycles++;
  }

  return hi;
}

function splitLineByWords(wordsArray, width, id, measureNode) {
  return binarySplit(wordsArray, width, id, measureNode, (arr, splitPoint) =>
    joinWords(arr, splitPoint)
  );
}

function splitWordByLetters(wordString, width, id, measureNode) {
  // TODO: for a single word case complex unicode symbols (emojis, Hindi, etc.) may be broken
  return binarySplit(wordString, width, id, measureNode, (str, splitPoint) =>
    str.substring(0, splitPoint)
  );
}

const getTextY = (y, height, fontSize, textHeight, vertAlignment) => {
  const top = y - fontSize * 0.175 + 0.15;
  // Formula: y + textHight + fontSize * 0.8
  // const middle = y + ((height - textHeight) / 2)+ fontSize*0,8;
  const middle = y - textHeight / 2 + height / 2 - fontSize * 0.2;
  // Formula: y + (height - texthHeight) / 2 + fontSize*0,8
  // const middle = y + ((height - textHeight) / 2) + fontSize*0.2;
  // const bottom = y + height - textHeight - fontSize * 0.2;
  const bottom = y + height - textHeight - fontSize * 0.175 - 0.15;
  // Formula: y + height - texthHeight - fontSize * 0.2
  // const middle = y + ((height - textHeight) / 2)+ fontSize*0,8;
  switch (vertAlignment) {
    case 'top':
      return top < middle ? top : middle;
    case 'middle':
      return middle;
    case 'bottom':
      return bottom > middle ? bottom : middle;
  }
};

const generateTSpanProps = (x, width, fontWidthFactor, horizontalAlignment, parentShield) => {
  const props = {};
  const textPadding = parentShield?.isMatrix ? 0.3 : 0;

  switch (horizontalAlignment) {
    case 'left':
      props.x = x + textPadding;
      props.textAnchor = 'start';
      break;
    case 'center':
      props.x = x + width / fontWidthFactor / 2;
      props.textAnchor = 'middle';
      break;
    case 'right':
      props.x = x + width / fontWidthFactor - textPadding;
      props.textAnchor = 'end';
      break;
    default:
      break;
  }
  return props;
};
